import * as z from 'zod/v4';

export const UNARY_AGGREGATE_OPERATORS = [
  'CountDistinct',
  'AnyValue',
  'Sum',
  'Count',
  'Min',
  'Max',
  'Avg',
  'MonthlyAvg',
  'Median',
  'Variance',
  'StdDev',
] as const;
export type UnaryAggregateOperator = (typeof UNARY_AGGREGATE_OPERATORS)[number];

export const AGGREGATE_OPERATORS = [
  ...UNARY_AGGREGATE_OPERATORS,
  'AvgByDimension',
  'PcafWeighted',
] as const;
export type AggregateOperator = (typeof AGGREGATE_OPERATORS)[number];

const MATH_OPERATORS = [
  'Add',
  'Subtract',
  'Multiply',
  'Divide',
  'Modulo',
  'Negate',
  'Abs',
  'Sqrt',
  'Floor',
  'Ceil',
  'Pow',
  'Log10',
  'Ln',
  'Round',
  'AsDecimal',
] as const;

const BOOLEAN_OPERATORS = [
  'Equals',
  'NotEquals',
  'DistinctFrom',
  'NotDistinctFrom',
  'LessThan',
  'LessThanOrEqual',
  'GreaterThan',
  'GreaterThanOrEqual',
  'In',
  'NotIn',
  'And',
  'Or',
  'Not',
  // Like SQL's BETWEEN, but with our current logic of inclusive start, exclusive end
  'TimeIntervalBetween',
  'IsNull',
  'IsNotNull',
] as const;

const STRING_OPERATORS = [
  'Concat',
  'Sha256',
  'Substr',
  'StartsWith',
  'EndsWith',
  'Contains',
  'Like',
  'ILike',
  'Upper',
  'Lower',
  'Replace',
  'StringSplit',
  // Custom non-standard operator
  'SentenceCase',
] as const;

const CONDITIONAL_OPERATORS = ['Coalesce', 'If', 'NullIf'] as const;

const CAST_OPERATORS = ['Cast'] as const;

const LIST_OPERATORS = ['ListContains', 'ListHasAny', 'ListToString'] as const;

const DATE_OPERATORS = [
  'DateDiff',
  'DateAdd',
  'DateTrunc',
  'Extract',
  // Custom non-standard operator
  'FiscalYear',
] as const;

// JSON operators
const JSON_OPERATORS = ['JsonExtractString', 'JsonExtract'] as const;

const CONVERSION_OPERATORS = [
  'ConvertUnit',
  'ConvertCurrency',
  'ConvertUnitName',
  'ConvertAggUnitName',
] as const;
// Table operators - these are non-standard operators that support table-level
// operations (a bit like SQL's window functions)
export const TABLE_OPERATORS = [
  // Apply an additional filter to the operand's underlying table
  'FilterTable',
  // Remove filters from the operand's underlying table, but leave grouping
  'UnfilterTable',
  // Remove grouping from the operand's underlying table, but leave filters
  'UngroupTable',
  // Remove filters and grouping from the operand's underlying table
  'TotalTable',
  // Operator that performs a top-N subquery, returning a list of strings
  'TopN',
  // Operator that selects a column from a subquery to be used as a list of values
  'SelectList',
] as const;

/**
 * To be precise about the semantics, DISTINCT_FOR_DIMENSION(expr, dimension1, dimension2, ...) is:
 * - Whenever this expression is aggregated, it will first group by the
 *   dependent dimensions (dimension1, dimension2, ...). The overall expression
 *   is "declaring" that the value is semantically distinct for each group, and
 *   a single value should be used during aggregation.
 *
 * Note: it's possible that the value will not actually be fixed within a
 * group, if there is a non-constant unit conversion (e.g. currencies). In
 * this case, we'll use the average value of the group.
 */
const SYMMETRIC_AGGREGATE_OPERATORS = ['DistinctForDimension'] as const;

/**
 * To be precise about the semantics, PCAF(A, B, C, D) is:
 * - We want a weighted average of B.
 * - First, group by A, weighting by C.
 * - Then, combine the groups, weighting by D.
 *
 * Note: if D changes within a group, we use the average D for that group.
 */
const FINANCE_COMPLEX_OPERATORS = ['PcafWeighted'] as const;

// Sort order operators - these are functional-style versions of
// the ASC and DESC keywords in a SQL order clause
export const SORT_ORDER_OPERATORS = ['Asc', 'Desc'] as const;

export const OperatorSchema = z.enum([
  ...AGGREGATE_OPERATORS,
  ...MATH_OPERATORS,
  ...BOOLEAN_OPERATORS,
  ...STRING_OPERATORS,
  ...CONDITIONAL_OPERATORS,
  ...CAST_OPERATORS,
  ...LIST_OPERATORS,
  ...DATE_OPERATORS,
  ...JSON_OPERATORS,
  ...CONVERSION_OPERATORS,
  ...SORT_ORDER_OPERATORS,
  ...TABLE_OPERATORS,
  ...SYMMETRIC_AGGREGATE_OPERATORS,
  ...FINANCE_COMPLEX_OPERATORS,
]);

/** Valid expression operator */
export type Operator = z.infer<typeof OperatorSchema>;

/** Enum of expression operators */
export const OPERATORS: { [T in Operator]: T } = OperatorSchema.enum;

// Row-level scalar types
export const SCALAR_TYPES = ['Number', 'String', 'Boolean', 'Date'] as const;
export type ScalarExpressionType = (typeof SCALAR_TYPES)[number];

// Row-level list types, used e.g. in IN expressions
export const SCALAR_LIST_TYPES = [
  'NumberList',
  'StringList',
  'BooleanList',
  'NullList',
] as const;

export type ListExpressionType = (typeof SCALAR_LIST_TYPES)[number];

// Aggregate types are returned by aggregate expressions. The most common is
// AggregateNumber, returned e.g. by SUM, but the others can all be returned
// by ANY_VALUE and in some cases by other operators like MIN and MAX
export const AGGREGATE_TYPES = [
  'AggregateNumber',
  'AggregateString',
  'AggregateBoolean',
  'AggregateDate',
] as const;

export type AggregateExpressionType = (typeof AGGREGATE_TYPES)[number];

// Literal types represent literal constants - strings, numbers, etc - in an expression
export const NON_NULL_LITERAL_TYPES = [
  'LiteralNumber',
  'LiteralString',
  'LiteralBoolean',
] as const;

export const LITERAL_TYPES = [
  ...NON_NULL_LITERAL_TYPES,
  'LiteralNull',
] as const;

export type LiteralExpressionType = (typeof LITERAL_TYPES)[number];

const LiteralTypeForScalar = {
  Number: 'LiteralNumber',
  String: 'LiteralString',
  Boolean: 'LiteralBoolean',
} as const satisfies Record<
  Exclude<ScalarExpressionType, 'Date'>,
  LiteralExpressionType
>;

/**
 * Get the literal type that corresponds to a scalar type
 */
export function literalTypeForScalar<
  T extends keyof typeof LiteralTypeForScalar,
>(scalarType: T): (typeof LiteralTypeForScalar)[T] {
  return LiteralTypeForScalar[scalarType];
}

const ScalarForLiteralType = {
  LiteralNumber: 'Number',
  LiteralString: 'String',
  LiteralBoolean: 'Boolean',
} as const satisfies Record<
  (typeof LiteralTypeForScalar)[keyof typeof LiteralTypeForScalar],
  keyof typeof LiteralTypeForScalar
>;

/**
 * Get the scalar type that corresponds to a literal type
 */
export function scalarForLiteralType<
  T extends keyof typeof ScalarForLiteralType,
>(literalType: T): (typeof ScalarForLiteralType)[T] {
  return ScalarForLiteralType[literalType];
}

const AggregateTypeForScalar = {
  Number: 'AggregateNumber',
  String: 'AggregateString',
  Boolean: 'AggregateBoolean',
  Date: 'AggregateDate',
} as const satisfies Record<ScalarExpressionType, AggregateExpressionType>;

export type AggregateTypeFor<K extends keyof typeof AggregateTypeForScalar> =
  (typeof AggregateTypeForScalar)[K];

/**
 * Get the aggregate type that corresponds to a scalar type
 */
export function aggregateTypeForScalar<
  T extends keyof typeof AggregateTypeForScalar,
>(scalarType: T): (typeof AggregateTypeForScalar)[T] {
  return AggregateTypeForScalar[scalarType];
}

const ScalarForAggregateType = {
  AggregateNumber: 'Number',
  AggregateString: 'String',
  AggregateBoolean: 'Boolean',
  AggregateDate: 'Date',
} as const satisfies Record<
  (typeof AggregateTypeForScalar)[keyof typeof AggregateTypeForScalar],
  keyof typeof AggregateTypeForScalar
>;

/**
 * Get the scalar type that corresponds to an aggregate type
 */
export function scalarForAggregateType<
  T extends keyof typeof ScalarForAggregateType,
>(aggregateType: T): (typeof ScalarForAggregateType)[T] {
  return ScalarForAggregateType[aggregateType];
}

const ListTypeForScalar = {
  Number: 'NumberList',
  String: 'StringList',
  Boolean: 'BooleanList',
} as const satisfies Record<
  Exclude<ScalarExpressionType, 'Date'>,
  ListExpressionType
>;

/**
 * Get the list type that corresponds to a scalar type
 */
export function listTypeForScalar<T extends keyof typeof ListTypeForScalar>(
  scalarType: T
): (typeof ListTypeForScalar)[T] {
  return ListTypeForScalar[scalarType];
}

const ScalarForListType = {
  NumberList: 'Number',
  StringList: 'String',
  BooleanList: 'Boolean',
} as const satisfies Record<
  (typeof ListTypeForScalar)[keyof typeof ListTypeForScalar],
  keyof typeof ListTypeForScalar
>;

/**
 * Get the scalar type that corresponds to a list type
 */
export function scalarForListType<T extends keyof typeof ScalarForListType>(
  listType: T
): (typeof ScalarForListType)[T] {
  return ScalarForListType[listType];
}

export const ExpressionTypeSchema = z.enum([
  ...SCALAR_TYPES,
  ...SCALAR_LIST_TYPES,
  ...AGGREGATE_TYPES,
  ...LITERAL_TYPES,
  // SortOrder is a special type only returned by ASC and DESC
  // expressions in the ORDER BY clause
  'SortOrder',
  // Unknown is only used for operators whose output isn't well-typed
  // (currently, only JSON_EXTRACT). The only operator that can accept
  // Unknown as input is CAST.
  'Unknown',
]);

/**
 * Data types represented/returned by expressions. Note that this is a custom list
 * that does not match the concrete types in DuckDB.
 * @useDeclaredType
 */
export type ExpressionType = z.infer<typeof ExpressionTypeSchema>;

/** Enum of expression data types */
export const EXPRESSION_TYPES: { [T in ExpressionType]: T } =
  ExpressionTypeSchema.enum;

/**
 * Valid SQL date part strings, treated as case-insensitive.
 * See [the DuckDB docs for date parts for reference.](https://duckdb.org/docs/stable/sql/functions/datepart.html#part-functions)
 */
export const DATE_PARTS = ['Year', 'Month', 'Day', 'Hour', 'Minute'] as const;

/**
 * Valid SQL date part string, treated as case-insensitive.
 * See [the DuckDB docs for date parts for reference.](https://duckdb.org/docs/stable/sql/functions/datepart.html#part-functions)
 * @useDeclaredType
 */
export type DatePart = (typeof DATE_PARTS)[number];
